export const BASE_PATH = '/sso';

export const apiUrl = (endpoint: string) => `${BASE_PATH}${endpoint}`;

export type CommonDataType = {
  url: string;
  config?: RequestInit;
};

export type PostDataType<TRequest> = CommonDataType & {
  body: TRequest;
};

export type ApiFetchOptions = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  config?: RequestInit;
};

export class ApiError extends Error {
  status: number;
  redirectUri?: string;
  error?: string;
  constructor(message: string, status: number, redirectUri?: string, error?: string) {
    super(message);
    this.status = status;
    this.redirectUri = redirectUri;
    this.error = error;
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export async function apiFetch<TResponse>({ url, method = 'GET', body, config }: ApiFetchOptions): Promise<TResponse> {
  const urlStr = url.startsWith(BASE_PATH) ? url : apiUrl(url);
  const headers = new Headers(config?.headers);
  if (body !== undefined && typeof body !== 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const fetchOptions: RequestInit = {
    credentials: 'include',
    ...config,
    method,
    headers,
    ...(body !== undefined ? { body: typeof body === 'string' ? body : JSON.stringify(body) } : {}),
  };

  let response: Response;
  try {
    response = await fetch(urlStr, fetchOptions);
  } catch (error) {
    console.error(getErrorMessage(error));
    throw new ApiError('Something went wrong', 500);
  }

  // Handle auth errors and transparent redirects to a login page (common in Spring Security)
  if (response.status === 401 || response.redirected) {
    throw new ApiError('Authentication required', 401);
  }

  if (response.status === 403) {
    throw new ApiError('Access forbidden', 403);
  }

  // Handle other errors (Fetch doesn't throw on 404/500, but TanStack Query needs it to)
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || errorData.error || 'Something went wrong',
      response.status,
      errorData.redirectUri,
      errorData.error
    );
  }

  const text = await response.text();
  if (!text) {
    return {} as TResponse;
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError('Invalid JSON response', 500);
  }
}

export async function postData<TRequest, TResponse>({ url, body, config }: PostDataType<TRequest>): Promise<TResponse> {
  return apiFetch<TResponse>({ url, method: 'POST', body, config });
}

export async function getData<TResponse>({ url, config }: CommonDataType): Promise<TResponse> {
  return apiFetch<TResponse>({ url, method: 'GET', config });
}
